import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { GlassButton } from 'dashboard/components/common';
import { API } from 'utilities/api';
import { useNavigate } from 'react-router-dom';
import { buildBlockTree } from 'dashboard/components/siteRenderer/blockStyles';
import LibraryBinding from '../LibraryBinding';
import BlockTree from './BlockTree';
import reorderSiblings from './reorderSiblings';
import AddBlockDialog from './AddBlockDialog';
import DetailPageOptions from './DetailPageOptions';

/**
 * Edits whatever is selected on the canvas.
 *
 * Order here is deliberate and was got wrong the first time. What the section
 * is called and whether it is on the live site come first, because those are
 * the two things someone checks at a glance. Colours, width and spacing fold
 * away — they are set once and then left alone. The content is last and open,
 * because it is what people came to change.
 */
export default function SectionInspector({
  section, catalog, brandId, page,
  onPatch, onComponentChange, onAddComponent,
}) {
  const navigate = useNavigate();
  const [mail, setMail] = useState(null);
  const [openBlockId, setOpenBlockId] = useState(null);
  // `null` closed; otherwise the parent to add into, with `false` meaning the
  // top level of the section.
  const [addInto, setAddInto] = useState(null);
  // { id, over } while a block is being dragged. Held here rather than in the
  // tree so a nested list and the top level share one drag.
  const [drag, setDrag] = useState(null);
  // Other published pages, so "the list lives on" is a pick from what exists.
  const [sitePages, setSitePages] = useState([]);
  const isContact = section?.type === 'contact';
  const isDetailHost = Boolean(page?.detailOf) && Boolean(section?.showDetailEntry);

  // Only fetched for a contact section — no reason to ask about mail config
  // while someone is recolouring a hero.
  useEffect(() => {
    if (!isContact || !brandId) return;
    API.getContactAddresses?.({ brand_id: brandId })
      .then(setMail)
      .catch(() => setMail({ addresses: [], configured: false }));
  }, [isContact, brandId]);

  useEffect(() => {
    if (!isDetailHost || !brandId) return;
    API.listSitePages?.({ brand_id: brandId })
      .then((res) => setSitePages((Array.isArray(res) ? res : []).filter((pg) => !pg.detailOf)))
      .catch(() => setSitePages([]));
  }, [isDetailHost, brandId]);

  // Which block types can hold other blocks is the server's answer, not a
  // second list kept here that could disagree with it.
  const containerTypes = useMemo(
    () => new Set((catalog?.components || []).filter((c) => c.container).map((c) => c.type)),
    [catalog],
  );

  const tree = useMemo(
    () => buildBlockTree(section?.componentData || []),
    [section],
  );

  if (!section) {
    return (
      <Box sx={{ p: '24px', textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant='body2' fontWeight='600' mb='4px'>Nothing selected</Typography>
        <Typography variant='caption'>
          Click any part of the page to edit it.
        </Typography>
      </Box>
    );
  }

  const bound = section.source?.kind === 'library';
  const hidden = section.isVisible === false;

  const typesForRegion = (catalog?.sections || []).filter((s) => {
    if (section.region === 'header') return ['header', 'custom'].includes(s.type);
    if (section.region === 'footer') return ['footer', 'custom'].includes(s.type);
    return !['header', 'footer'].includes(s.type);
  });

  const siblingsOf = (node) => (node.parent_id
    ? (findNode(tree, node.parent_id)?.children || [])
    : tree);

  /** Move a block among its own siblings. */
  const moveBlock = (node, delta) => {
    const siblings = siblingsOf(node);
    const i = siblings.findIndex((s) => s._id === node._id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= siblings.length) return;
    const ids = siblings.map((s) => s._id);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    onComponentChange('reorder', {
      parent_id: node.parent_id || null,
      orderedIds: ids,
    });
  };

  /**
   * Drop one block onto another.
   *
   * Only among siblings, which is the same rule sections follow on the canvas
   * — a block cannot leave its parent by being dragged, because that is a
   * different operation (re-parenting) with different consequences, and one
   * you would trigger by accident on the way past.
   *
   * The dragged block takes the target's place rather than swapping with it:
   * dragging the first block onto the fourth should make it fourth, not trade
   * the two. Swapping is what a naive implementation does and it surprises
   * people every time.
   */
  const dropBlock = (dragId, dropId) => {
    if (!dragId || !dropId || dragId === dropId) return;
    const dragged = findNode(tree, dragId);
    const target = findNode(tree, dropId);
    if (!dragged || !target) return;
    if ((dragged.parent_id || null) !== (target.parent_id || null)) return;

    const ids = reorderSiblings(siblingsOf(dragged).map((s) => s._id), dragId, dropId);
    if (!ids) return;
    onComponentChange('reorder', {
      parent_id: dragged.parent_id || null,
      orderedIds: ids,
    });
  };

  return (
    <Box sx={{ p: '16px' }}>
      {/* ── What it is, and whether anyone can see it ─────────────────── */}
      <Stack direction='row' alignItems='center' spacing='8px' mb='8px' flexWrap='wrap'>
        <Chip size='small' color='secondary' label={section.region} />
        <Typography variant='caption' color='text.secondary'>{section.type}</Typography>
      </Stack>

      <TextField
        size='small'
        value={section.name || ''}
        onChange={(e) => onPatch({ name: e.target.value })}
        placeholder={section.type}
        label='Section name'
        helperText='Only you see this.'
        fullWidth
        sx={{ mb: '10px' }}
      />

      <FormControlLabel
        sx={{ mb: hidden ? '8px' : '12px' }}
        control={
          <Switch
            size='small'
            checked={!hidden}
            onChange={(e) => onPatch({ isVisible: e.target.checked })}
          />
        }
        label={
          <Typography variant='body2' fontWeight={hidden ? 700 : 400}>
            {hidden ? 'Hidden from your live site' : 'Showing on your live site'}
          </Typography>
        }
      />

      {hidden && (
        <Alert severity='info' sx={{ mb: '12px', py: '4px' }}>
          <Typography variant='caption'>
            New sections start hidden so half-built work never reaches
            visitors. Switch it on when it is ready.
          </Typography>
        </Alert>
      )}

      {/* ── Set once, then left alone ─────────────────────────────────── */}
      <Accordion
        disableGutters
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant='body2' fontWeight='600'>Look and spacing</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing='12px'>
            <FormControl size='small' fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                label='Type'
                value={section.type}
                onChange={(e) => onPatch({ type: e.target.value, version: 'v1' })}
              >
                {typesForRegion.map((s) => (
                  <MenuItem key={s.type} value={s.type}>{s.type}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction='row' spacing='12px'>
              <ColorField
                label='Background'
                value={section.backgroundColor && section.backgroundColor !== 'transparent'
                  ? section.backgroundColor : '#ffffff'}
                onChange={(v) => onPatch({ backgroundColor: v })}
              />
              <ColorField
                label='Text'
                value={section.textColor || '#111827'}
                onChange={(v) => onPatch({ textColor: v })}
              />
            </Stack>

            <FormControl size='small' fullWidth>
              <InputLabel>Width</InputLabel>
              <Select
                label='Width'
                value={section.layout || 'contained'}
                onChange={(e) => onPatch({ layout: e.target.value })}
              >
                <MenuItem value='contained'>Normal — follows the section type</MenuItem>
                <MenuItem value='boxed'>Narrow — easier to read</MenuItem>
                <MenuItem value='full'>Full width — edge to edge</MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Typography variant='caption' color='text.secondary' display='block' mb='2px'>
                Space above — {section.paddingTop ?? 'default'}
              </Typography>
              <Slider
                size='small'
                min={0}
                max={160}
                step={8}
                value={Number(section.paddingTop) || 0}
                onChange={(_e, v) => onPatch({ paddingTop: v })}
                valueLabelDisplay='auto'
              />
              <Typography variant='caption' color='text.secondary' display='block' mb='2px'>
                Space below — {section.paddingBottom ?? 'default'}
              </Typography>
              <Slider
                size='small'
                min={0}
                max={160}
                step={8}
                value={Number(section.paddingBottom) || 0}
                onChange={(_e, v) => onPatch({ paddingBottom: v })}
                valueLabelDisplay='auto'
              />
              {(section.paddingTop || section.paddingBottom) ? (
                <GlassButton
                  size='small'
                  onClick={() => onPatch({ paddingTop: 0, paddingBottom: 0 })}
                >
                  Back to default spacing
                </GlassButton>
              ) : null}
            </Box>

            {page?.detailOf && !bound && (
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={!!section.showDetailEntry}
                    onChange={(e) => onPatch({ showDetailEntry: e.target.checked })}
                  />
                }
                label={<Typography variant='body2'>Show the blog post / entry here</Typography>}
              />
            )}

            {isContact && (
              <Box>
                <Typography variant='caption' fontWeight='700' color='text.secondary' textTransform='uppercase' display='block' mb='8px'>
                  Enquiries go to
                </Typography>
                {mail?.configured ? (
                  <FormControl size='small' fullWidth>
                    <InputLabel shrink>Email address</InputLabel>
                    <Select
                      displayEmpty
                      label='Email address'
                      value={section.contactEmail || ''}
                      onChange={(e) => onPatch({ contactEmail: e.target.value })}
                    >
                      <MenuItem value=''>Your default address</MenuItem>
                      {(mail.addresses || []).map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                    </Select>
                  </FormControl>
                ) : (
                  <Box>
                    <Typography variant='body2' color='warning.main' mb='8px'>
                      No sending address set up yet, so this form has nowhere to send enquiries.
                    </Typography>
                    <GlassButton size='small' onClick={() => navigate('/dashboard/mail/manager')}>
                      Set one up in Email manager
                    </GlassButton>
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {isDetailHost && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{ mt: '10px', border: '1px solid', borderColor: 'divider', borderRadius: '10px', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant='body2' fontWeight='600'>Around the article</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DetailPageOptions
              options={section.detailOptions}
              pages={sitePages}
              onChange={(detailOptions) => onPatch({ detailOptions })}
            />
          </AccordionDetails>
        </Accordion>
      )}

      <Divider sx={{ my: '16px' }} />

      {/* ── What is in it ─────────────────────────────────────────────── */}
      {bound ? (
        <LibraryBinding source={section.source} onPatch={onPatch} brandId={brandId} />
      ) : (
        <Box>
          <Stack direction='row' alignItems='center' justifyContent='space-between' mb='10px' gap='8px'>
            <Typography variant='caption' fontWeight='700' color='text.secondary' textTransform='uppercase'>
              Content
            </Typography>
            <GlassButton size='small' startIcon={<AddIcon />} onClick={() => setAddInto(false)}>
              Add
            </GlassButton>
          </Stack>

          {tree.length === 0 && (
            <Typography variant='body2' color='text.secondary' fontStyle='italic'>
              Nothing in this section yet.
            </Typography>
          )}

          <BlockTree
            nodes={tree}
            openId={openBlockId}
            onToggleOpen={setOpenBlockId}
            onMove={moveBlock}
            onDropBlock={dropBlock}
            drag={drag}
            setDrag={setDrag}
            onAddInside={(node) => setAddInto(node)}
            onDelete={(node) => onComponentChange('delete', { component_id: node._id })}
            onPatch={(node, patch) => onComponentChange('update', { component_id: node._id, patch })}
            catalog={catalog}
            brandId={brandId}
            containerTypes={containerTypes}
          />

          <AddBlockDialog
            open={addInto !== null}
            insideLabel={addInto ? (addInto.name || addInto.type) : ''}
            onClose={() => setAddInto(null)}
            onPick={(type) => onAddComponent(section, type, addInto ? addInto._id : null)}
          />
        </Box>
      )}
    </Box>
  );
}

/** Depth-first lookup, used to find a moved block's siblings. */
function findNode(nodes, id) {
  for (const n of nodes) {
    if (String(n._id) === String(id)) return n;
    const hit = findNode(n.children || [], id);
    if (hit) return hit;
  }
  return null;
}

function ColorField({ label, value, onChange }) {
  return (
    <Box display='flex' flexDirection='column' flex={1}>
      <Typography variant='caption' color='text.secondary' mb='4px'>{label}</Typography>
      <Box
        component='input'
        type='color'
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          width: '100%', height: 36, padding: '2px', border: '1px solid',
          borderColor: 'divider', borderRadius: '8px', background: 'transparent',
          cursor: 'pointer',
        }}
      />
    </Box>
  );
}
